import { Alert, Box, Button, Container, FormControl, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import InputFormik from "../components/inputs/InputFormik";
import useDeleteAccountFormik from "../hooks/useDeleteAccountHook";

const DeleteAccountRoute = () => {

  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get("email") || "";
  
  const {
    values,
    errors,
    touched,
    loading,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useDeleteAccountFormik(emailFromQuery);
  

  const handleDeleteClick = () => {
    // Crear un evento sintético para handleSubmit
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
    } as React.FormEvent<HTMLFormElement>;
    handleSubmit(syntheticEvent);
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: {
          xs: "100%",
          sm: "100%",
          md: "75%",
          lg: "75%",
        },
        padding: 4,
        justifyContent: "center",
        gap: 1,
      }}
    >
      <img
        style={{ margin: ".25rem auto", width: 250 }}
        alt="logo"
        src={"/logo.png"}
      />
      <Typography
        sx={{
          color: (theme) => theme.palette.text.primary,
          fontWeight: 900,
          fontSize: {
            xs: 20,
            sm: 20,
            md: 25,
            lg: 25,
          },
          textAlign: "center",
          marginBottom: 2,
        }}
      >
        Eliminar cuenta
      </Typography>

      <Alert severity="error" sx={{ mb: 3, mx: "auto", maxWidth: "600px" }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
          ⚠️ Advertencia: Esta operación es irreversible
        </Typography>
        <Typography variant="body2" component="div">
          Al eliminar tu cuenta, se procederá a:
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            <li>Eliminar todos los paquetes asociados a tu cuenta</li>
            <li>Eliminar tu correo electrónico y contraseña</li>
            <li>Eliminar todos tus datos personales</li>
            <li>Eliminar cualquier rastro de tu información en el sistema</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
            Una vez completada la eliminación, no podrás recuperar tu cuenta ni
            tus datos. Esta acción es permanente e irreversible.
          </Typography>
        </Typography>
      </Alert>

      <Box
        sx={{
          width: {
            xs: "100%",
            sm: "80%",
            md: "60%",
            lg: "40%",
          },
          mx: "auto",
          mb: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.text.secondary,
            mb: 2,
            textAlign: "center",
          }}
        >
          Para eliminar tu cuenta, sigue estos pasos:
        </Typography>
        <Box
          component="ol"
          sx={{
            pl: 2,
            mb: 3,
            "& li": {
              mb: 1,
            },
          }}
        >
          <Typography component="li" variant="body2">
            Ingresa tu correo electrónico en el campo a continuación
          </Typography>
          <Typography component="li" variant="body2">
            Ingresa tu contraseña para confirmar tu identidad
          </Typography>
          <Typography component="li" variant="body2">
            Haz clic en "Eliminar cuenta" para confirmar la eliminación
          </Typography>
          <Typography component="li" variant="body2">
            Tu cuenta y todos tus datos serán eliminados permanentemente
          </Typography>
        </Box>
      </Box>

      <FormControl
        sx={{
          width: {
            xs: "100%",
            sm: "80%",
            md: "60%",
            lg: "40%",
          },
          mx: "auto",
          gap: 2,
        }}
      >
        <InputFormik
          label="Correo electrónico"
          name="email"
          value={values.email}
          error={!!touched.email && !!errors.email}
          errorText={errors.email ?? ""}
          handleChange={handleChange("email")}
          handleBlur={handleBlur("email")}
          type="email"
          autoCapitalize="none"
        />

        <InputFormik
          label="Contraseña"
          name="password"
          value={values.password}
          error={!!touched.password && !!errors.password}
          errorText={errors.password ?? ""}
          handleChange={handleChange("password")}
          handleBlur={handleBlur("password")}
          type="password"
        />

        <Button
          variant="contained"
          onClick={handleDeleteClick}
          disabled={loading || !!errors.email || !!errors.password || !values.email || !values.password}
          sx={{
            fontWeight: 900,
            borderRadius: 10,
            backgroundColor: (theme) => theme.palette.error.main,
            color: (theme) => theme.palette.error.contrastText,
            fontSize: {
              xs: 12,
              sm: 12,
              md: 16,
              lg: 16,
            },
            "&:hover": {
              backgroundColor: (theme) => theme.palette.error.dark,
            },
          }}
        >
          {loading ? "Eliminando cuenta..." : "Eliminar cuenta"}
        </Button>
      </FormControl>
    </Container>
  );
};

export default DeleteAccountRoute;

